import { IonContent, IonLoading, IonModal } from "@ionic/react";
import { Box } from "@mui/material";
import { ShiftFilters } from "@src/app/components/shiftFilters";
import { useAgentPreferences } from "@src/appV2/Agents/api/useAgentPreferences";
import { useAgentProfile } from "@src/appV2/Agents/api/useAgentProfile";
import { useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { calculateGeoDistanceInMiles, convertToGeoLocation } from "@src/appV2/Location";
import {
  DISTANCE_PREFERENCE_MAX_VALUE,
  HCF_PROFILE_SOURCE,
  SEARCH_MODE,
  USER_EVENTS,
} from "@src/constants";
import { Facility } from "@src/lib/interface";
import { computeShiftsCountMap } from "@src/utils/openShifts";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { fetchFacilityOpenShiftCount } from "./api";
import { FETCH_FACILITY_OPEN_SHIFT_COUNT_ENDPOINT } from "./api/api";
import { GoogleMapShifts } from "./map";
import { RequestOpenShiftOptionsMap } from "./model";
import { FacilityDetails } from "../dayView/facilityDetails";
import { useDefinedAgent } from "../store/helperHooks";
import { clearOpenShiftItems } from "../store/shift";

interface OpenShiftMapInterface {
  segmentView: string;
}

export function OpenShiftMap(props: OpenShiftMapInterface) {
  const { segmentView } = props;
  const agent = useDefinedAgent();

  const { setFilters } = useAgentPreferences();
  const { data: agentProfile } = useAgentProfile();

  const dispatch = useDispatch();
  const { showErrorToast } = useToast();

  const [facilityDetails, setFacilitydetails] = useState<Facility | null>(null);
  const [currentMapCenterCoordinates, setCurrentMapCenterCoordinates] = useState<number[]>(
    agent?.geoLocation?.coordinates ?? []
  );
  const isMapView = segmentView === "map";
  const distancePreference = agent?.preference?.distance ?? DISTANCE_PREFERENCE_MAX_VALUE;
  const qualificationPreference = agent?.preference?.qualification ?? "";

  const openMapFacilityDetails = (facility: Facility) => {
    logEvent(USER_EVENTS.TAP_FACILITY_PIN, {
      shifts: facility.shiftsCount || 0,
    });
    setFacilitydetails(facility);
  };

  const closeFacilityDetails = () => {
    setFacilitydetails(null);
    dispatch(clearOpenShiftItems());
  };

  const dateRange = {
    start: moment().add(1, "minutes").format("YYYY-MM-DD"),
    end: moment().add(30, "days").format("YYYY-MM-DD"),
  };
  const apiOptions: RequestOpenShiftOptionsMap = {
    range: dateRange,
    coordinates: currentMapCenterCoordinates ?? [],
    distance: distancePreference,
    qualification: qualificationPreference,
    specialities: agent.specialities ?? {},
  };

  const { data: facilityShifts = [], isLoading: facilityShiftsAreLoading } = useQuery<
    Facility[],
    Error
  >({
    queryKey: [FETCH_FACILITY_OPEN_SHIFT_COUNT_ENDPOINT, apiOptions],
    queryFn: () => fetchFacilityOpenShiftCount(apiOptions),
    onError: () => {
      showErrorToast("Failed to fetch map openShifts data, please try again");
    },
    enabled: isMapView,
  });

  useEffect(() => {
    /**
     * This is to clear the state of the open shift items.
     * This will refresh the shift card items, allowing
     * workers to re-check if they can book the shifts.
     */
    dispatch(clearOpenShiftItems());
  }, [dispatch]);

  const onClickFindShiftsButton = (coordinates: number[]): void => {
    setCurrentMapCenterCoordinates(coordinates);
  };

  const getSearchMode = () => {
    if (!facilityDetails?.geoLocation) {
      return;
    }
    const facilityGeoLocation = convertToGeoLocation(facilityDetails.geoLocation.coordinates);
    const agentGeoLocation = convertToGeoLocation(agent?.geoLocation?.coordinates ?? [0, 0]);

    const distance = calculateGeoDistanceInMiles(agentGeoLocation, facilityGeoLocation);
    if (distance > DISTANCE_PREFERENCE_MAX_VALUE) {
      return SEARCH_MODE.NON_QFA_RANGE_SHIFTS;
    }
  };

  return (
    <>
      <IonContent data-testid="open-shifts-map">
        <IonLoading isOpen={facilityShiftsAreLoading && isMapView} backdropDismiss={true} />
        <div className="open-shift-map-container">
          {/* fixme */}
          <Box paddingY={1}>
            <ShiftFilters
              totalShifts={computeShiftsCountMap(facilityShifts)}
              distancePreference={distancePreference}
              qualificationPreference={qualificationPreference}
              searchMode={SEARCH_MODE.MAP}
              facilityShiftIsLoading={facilityShiftsAreLoading}
              setFilters={setFilters}
              agentLicenses={agentProfile?.licensesData ?? []}
            />
          </Box>
          <GoogleMapShifts
            segmentView={segmentView}
            onClickHcfMarker={openMapFacilityDetails}
            facilityShiftsAreLoading={facilityShiftsAreLoading}
            facilityShifts={facilityShifts}
            onClickFindShiftsButton={onClickFindShiftsButton}
            currentMapCenterCoordinates={currentMapCenterCoordinates}
          />
        </div>
      </IonContent>
      <IonModal isOpen={!!facilityDetails} onDidDismiss={closeFacilityDetails}>
        {facilityDetails && (
          <FacilityDetails
            facility={facilityDetails as Facility}
            onClose={closeFacilityDetails}
            shift={{}}
            hcfProfileSource={HCF_PROFILE_SOURCE.MAP}
            searchMode={getSearchMode()}
          />
        )}
      </IonModal>
    </>
  );
}
